import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { Card, Table } from 'antd';
import { ColumnType } from 'antd/es/table';
import i18n from 'i18n';
import dayjs from 'dayjs';
import { ConsignmentReportType, consignmentStatisticsType } from 'types/report';
import LOCALS from 'commons/locals';
import { thousands } from 'utils/tools';
import { PaymentMethod } from 'constants/RecyclingConsignment';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { ShoppingBag } from 'lucide-react';

export interface ConsignmentDailyReportRef {
  getSheetData: () => {
    headers: string[];
    rows: (string | number)[][];
    stats: (string | number)[][];
  };
}

interface ConsignmentDailyReportProps {
  loading: boolean;
  records?: ConsignmentReportType[];
  statistics?: consignmentStatisticsType;
  currencySwitch: (value: number) => string;
}

const ConsignmentDailyReport = forwardRef<
  ConsignmentDailyReportRef,
  ConsignmentDailyReportProps
>(({ loading, records, statistics, currencySwitch }, ref) => {
  const { staffSelectOptions } = useAppSelector(selectGlobalInfo);

  const columnsRender = useCallback(
    () => ({
      publicStaff: (createdBy: number | string | null) =>
        staffSelectOptions.find((d) => d.value === Number(createdBy))?.label ||
        createdBy,
    }),
    [staffSelectOptions]
  );

  const columns: Array<
    ColumnType<ConsignmentReportType> & { hidden?: boolean }
  > = useMemo(() => {
    return [
      {
        title: i18n.t('consignment_date'),
        dataIndex: 'date',
        key: 'date',
        render: (date: ConsignmentReportType['date']) =>
          dayjs(date).format('YYYY-MM-DD'),
      },
      {
        title: i18n.t('consignment_product_id'),
        dataIndex: 'productId',
        key: 'productId',
        render: (productId: ConsignmentReportType['productId']) => (
          <a href={`/pms/product-view/${productId}`}>{productId}</a>
        ),
      },
      {
        title: i18n.t(LOCALS.product_name),
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: i18n.t(LOCALS.sHXnOEVSrC),
        dataIndex: 'submissionId',
        key: 'submissionId',
        render: (submissionId: ConsignmentReportType['submissionId']) =>
          submissionId || '-',
      },
      {
        title: i18n.t('consignment_consignor'),
        dataIndex: 'consignor',
        key: 'consignor',
        render: (consignor: ConsignmentReportType['consignor']) => (
          <a href={`/ums/member-view/${consignor}`}>{consignor}</a>
        ),
      },
      {
        hidden: true,
        title: i18n.t('currency'),
        dataIndex: 'currency',
        key: 'currency',
      },
      {
        title: i18n.t('consignment_contract_price'),
        dataIndex: 'costPrice',
        key: 'costPrice',
        render: (
          costPrice: ConsignmentReportType['costPrice'],
          record: ConsignmentReportType
        ) => `${record.currency} ${thousands(costPrice)}`,
      },
      {
        title: i18n.t('consignment_selling_price'),
        dataIndex: 'price',
        key: 'price',
        render: (
          price: ConsignmentReportType['price'],
          record: ConsignmentReportType
        ) => `${record.currency} ${thousands(price)}`,
      },
      {
        title: i18n.t('consignment_payment_method'),
        dataIndex: 'paymentMethod',
        key: 'paymentMethod',
        render: (paymentMethod: ConsignmentReportType['paymentMethod']) =>
          PaymentMethod.find((d) => d.value === paymentMethod)?.label,
      },
      {
        title: i18n.t('consignment_responsible'),
        dataIndex: 'owner',
        key: 'owner',
        render: (owner: ConsignmentReportType['owner']) =>
          columnsRender().publicStaff(owner),
      },
      {
        title: i18n.t('consignment_notes'),
        dataIndex: 'remark',
        key: 'remark',
      },
    ];
  }, [columnsRender]);

  const stats = useMemo(() => {
    if (!statistics) return null;
    const items = [
      {
        name: i18n.t('consignment_total_purchase_quantity'),
        value: thousands(statistics.totalPurchaseQuantity),
        key: 'totalPurchaseQuantity',
      },
      {
        name: i18n.t('consignment_total_cost_price'),
        value: currencySwitch(statistics.totalCostPrice),
        key: 'totalCostPrice',
      },
      {
        name: i18n.t('consignment_total_price'),
        value: currencySwitch(statistics.totalPrice),
        key: 'totalPrice',
      },
    ];
    return items;
  }, [currencySwitch, statistics]);

  const renderStats = useCallback(() => {
    if (!stats) return null;
    return (
      <div className="mb-4 mt-4">
        <div className="ant-row ant-row-middle" style={{ rowGap: 16 }}>
          <div className="ant-col" style={{ flex: 'auto' }}>
            <div
              className="ant-card ant-card-small rounded-lg"
              style={{ border: 'none' }}
            >
              <div
                className="ant-card-body"
                style={{ padding: 16, backgroundColor: '#EBF5FF' }}
              >
                <div
                  className="w-full grid gap-4"
                  style={{
                    gridTemplateColumns:
                      'repeat(auto-fit, minmax(160px, 1fr))',
                  }}
                >
                  {stats.map((d, index) => (
                    <div
                      key={index}
                      className="flex justify-center items-center flex-col"
                    >
                      <div className="text-sm text-gray-600 text-center mb-1">
                        {d.name}
                      </div>
                      <div className="text-lg font-semibold text-blue-700">
                        {d.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [stats]);

  const getRows = useCallback(() => {
    if (!records) return [] as (string | number)[][];
    return records.map((d) => {
      const owner = columnsRender().publicStaff(d.owner);
      return [
        dayjs(d.date).format('YYYY-MM-DD'),
        d.productId,
        d.name,
        d.submissionId || '-',
        d.consignor,
        d.currency,
        `${thousands(d.costPrice)}`,
        `${thousands(d.price)}`,
        PaymentMethod.find((dd) => dd.value === d.paymentMethod)?.label || '',
        owner as string,
        d.remark,
      ];
    });
  }, [records, columnsRender]);

  useImperativeHandle(ref, () => ({
    getSheetData: () => ({
      headers: columns.map((d) => d.title?.toString() || ''),
      rows: getRows(),
      stats:
        stats?.map((d) => [d.name as string, d.value as string | number]) || [],
    }),
  }));

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center gap-2 mb-4 text-base font-medium">
        <ShoppingBag className="w-5 h-5" />
        <div className="text-base font-medium">
          {i18n.t('consignment_daily_report')}
        </div>
      </div>
      <Table
        size="small"
        bordered
        tableLayout="auto"
        loading={loading}
        pagination={false}
        columns={columns.filter((d) => !(d as any).hidden)}
        dataSource={records}
        rowKey={(_, index) => index || 0}
        scroll={{ x: 'max-content' }}
      />
      {renderStats()}
    </Card>
  );
});

export default ConsignmentDailyReport;
